import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  InputAdornment,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Email as EmailIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { usePermissions } from '../../../../utils/permissions';

export default function InvitationsTab() {
  const theme = useTheme();
  const { canEditPage, canAccessPage, companyId, canInviteUsers, hierarchyLevel, userRole } = usePermissions();
  
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitationsPage, setInvitationsPage] = useState(0);
  const [invitationsRowsPerPage, setInvitationsRowsPerPage] = useState(10);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role_id: '' });
  const [roleSelectionDialogOpen, setRoleSelectionDialogOpen] = useState(false);
  const [roleSelectionSearchTerm, setRoleSelectionSearchTerm] = useState('');
  const [roleSelectionHierarchyFilter, setRoleSelectionHierarchyFilter] = useState('');
  const [selectedRoleForDialog, setSelectedRoleForDialog] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });

  useEffect(() => {
    if (companyId) {
      loadInvitations();
      loadRoles();
    }
  }, [companyId]);

  const loadInvitations = async () => {
    const { data, error } = await supabase
      .from('user_invitations')
      .select(`
        id,
        email,
        status,
        invited_by,
        expires_at,
        created_at,
        role_id,
        roles!inner (
          id,
          display_name
        )
      `)
      .eq('company_id', companyId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) throw error;
    setInvitations(data || []);
  };

  const loadRoles = async () => {
    const { data: rolesData, error } = await supabase
      .from('roles')
      .select('*')
      .or(`company_id.eq.${companyId},is_system_role.eq.true`)
      .order('hierarchy_level', { ascending: true });

    if (error) throw error;
    setRoles(rolesData || []);
  };

  const showAlert = (message, severity = 'info') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 5000);
  };

  const handleInviteUser = async () => {
    try {
      if (!inviteForm.email || !inviteForm.role_id) {
        showAlert('Please provide an email and select a role.', 'warning');
        return;
      }

      const { data: validationResult, error: validationError } = await supabase
        .rpc('validate_invitation', { 
          p_email: inviteForm.email,
          p_company_id: companyId 
        });

      if (validationError) {
        console.error('Error validating invitation:', validationError);
        showAlert('Error validating invitation. Please try again.', 'error');
        return;
      }

      if (validationResult && validationResult.length > 0) {
        const validation = validationResult[0];
        if (!validation.is_valid) {
          showAlert(validation.error_message, 'warning');
          return;
        }
      }

      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: inviteForm.email,
          role_id: inviteForm.role_id,
          company_id: companyId
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        showAlert('Failed to send invitation. Please check the email address and try again.', 'warning');
        return;
      }

      if (data?.error) {
        showAlert(data.error, 'error');
        return;
      }

      showAlert(data?.message || 'Invitation sent!', 'success');
      setInviteDialogOpen(false);
      setInviteForm({ email: '', role_id: '' });
      await loadInvitations();
    } catch (err) {
      console.error('Invitation error:', err);
      
      if (err.message && err.message.includes('Edge Function returned a non-2xx status code')) {
        showAlert('Failed to send invitation. Please check the email address and try again.', 'error');
      } else {
        showAlert(err.message || 'Failed to send invitation', 'error');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleResendInvitation = async (invitation) => {
    // Resend functionality removed
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      const { error } = await supabase
        .from('user_invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId);

      if (error) throw error;

      showAlert('Invitation cancelled successfully!', 'success');
      await loadInvitations();
    } catch (error) {
      showAlert('Error cancelling invitation: ' + error.message, 'error');
    }
  };

  const handleInvitationsChangePage = (event, newPage) => {
    setInvitationsPage(newPage);
  };

  const handleInvitationsChangeRowsPerPage = (event) => {
    setInvitationsRowsPerPage(parseInt(event.target.value, 10));
    setInvitationsPage(0);
  };

  const openRoleSelectionDialog = () => {
    setRoleSelectionDialogOpen(true);
    setRoleSelectionSearchTerm('');
    setRoleSelectionHierarchyFilter('');
    setSelectedRoleForDialog(null);
  };

  const closeRoleSelectionDialog = () => {
    setRoleSelectionDialogOpen(false);
    setRoleSelectionSearchTerm('');
    setRoleSelectionHierarchyFilter('');
    setSelectedRoleForDialog(null);
  };

  const handleRoleSelection = (role) => {
    setSelectedRoleForDialog(role);
  };

  const confirmRoleSelection = () => {
    if (selectedRoleForDialog) {
      setInviteForm({ ...inviteForm, role_id: selectedRoleForDialog.id });
    }
    closeRoleSelectionDialog();
  };

  const getFilteredRolesForSelection = () => {
    let filteredRoles = roles.filter(role => {
      if (role.hierarchy_level === 1) {
        return false;
      }
      
      return hierarchyLevel < role.hierarchy_level || userRole === 'super_admin';
    });

    if (roleSelectionHierarchyFilter) {
      filteredRoles = filteredRoles.filter(role => 
        role.hierarchy_level.toString() === roleSelectionHierarchyFilter
      );
    }

    if (roleSelectionSearchTerm && roleSelectionSearchTerm.trim()) {
      const searchTerm = roleSelectionSearchTerm.toLowerCase().trim();
      filteredRoles = filteredRoles.filter(role => {
        const name = (role.name || '').toLowerCase();
        const displayName = (role.display_name || '').toLowerCase();
        const description = (role.description || '').toLowerCase();
        
        return name.includes(searchTerm) || 
               displayName.includes(searchTerm) || 
               description.includes(searchTerm);
      });
    }

    const grouped = {};
    filteredRoles.forEach(role => {
      if (!grouped[role.hierarchy_level]) {
        grouped[role.hierarchy_level] = [];
      }
      grouped[role.hierarchy_level].push(role);
    });

    return grouped;
  };

  const getRoleStatusColor = (hierarchyLevel) => {
    switch (hierarchyLevel) {
      case 1: return 'error';
      case 2: return 'warning';
      case 3: return 'info';
      case 4: return 'success';
      default: return 'default';
    }
  };

  const getHierarchyLevelName = (level) => {
    switch (level) {
      case 1: return 'Super Administrator Level';
      default: return `Level ${level}`;
    }
  };

  if (!canAccessPage('invitations')) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Card sx={{
          p: 6,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 500,
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
            zIndex: 0,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <EmailIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Invitations Management Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            You don't currently have the necessary permissions to view invitations. Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {alert.show && (
        <Alert 
          severity={alert.severity} 
          sx={{ mb: 3, borderRadius: 3 }}
          onClose={() => setAlert({ show: false, message: '', severity: 'info' })}
        >
          {alert.message}
        </Alert>
      )}

      {/* Header with buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 3 }}>
        {canEditPage('invitations') && canInviteUsers && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setInviteDialogOpen(true)}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.2,
              fontSize: '0.9rem',
              fontWeight: 500,
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              backdropFilter: 'blur(10px)',
              color: 'text.secondary',
              '&:hover': {
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                transform: 'translateY(-1px)'
              }
            }}
          >
            Invite User
          </Button>
        )}
      </Box>

      <Paper sx={{ 
        borderRadius: 3, 
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <TableContainer sx={{ overflow: 'visible' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3
                  }}
                >
                  Email
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3
                  }}
                >
                  Role
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3
                  }}
                >
                  Status
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3
                  }}
                >
                  Invited
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3
                  }}
                >
                  Expires
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3,
                    width: 120
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
          <TableBody>
            {(() => {
              const pendingInvitations = invitations.filter(invitation => invitation.status === 'pending');
              
              if (pendingInvitations.length === 0) {
                return (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                      <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        No current invitations
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                        Pending invitations will appear here
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              }
              
              const paginatedInvitations = invitationsRowsPerPage > 0
                ? pendingInvitations.slice(invitationsPage * invitationsRowsPerPage, invitationsPage * invitationsRowsPerPage + invitationsRowsPerPage)
                : pendingInvitations;
              
              return paginatedInvitations.map((invitation, index) => (
                <TableRow 
                  key={invitation.id}
                  hover
                  sx={{ 
                    cursor: 'default',
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      bgcolor: 'grey.50',
                      transform: 'translateX(4px)',
                      transition: 'all 0.2s ease',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                      py: 2.5,
                      px: 3
                    }
                  }}
                >
                  <TableCell>
                    <Typography sx={{ fontWeight: 500 }}>
                      {invitation.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={invitation.roles.display_name}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={invitation.status}
                      color="warning"
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    {formatDate(invitation.created_at)}
                  </TableCell>
                  <TableCell>
                    {formatDate(invitation.expires_at)}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title={canEditPage('invitations') && canInviteUsers ? "Cancel Invitation" : "No permission to manage invitations"}>
                        <span>
                          <IconButton 
                            size="small"
                            onClick={() => canEditPage('invitations') && canInviteUsers && handleCancelInvitation(invitation.id)}
                            disabled={!canEditPage('invitations') || !canInviteUsers}
                            sx={{
                              bgcolor: canEditPage('invitations') && canInviteUsers ? alpha(theme.palette.error.main, 0.08) : alpha(theme.palette.grey[400], 0.08),
                              border: canEditPage('invitations') && canInviteUsers ? `1px solid ${alpha(theme.palette.error.main, 0.2)}` : `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                              '&:hover': {
                                bgcolor: canEditPage('invitations') && canInviteUsers ? alpha(theme.palette.error.main, 0.15) : alpha(theme.palette.grey[400], 0.08)
                              },
                              '&:disabled': {
                                bgcolor: alpha(theme.palette.grey[400], 0.08),
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                              }
                            }}
                          >
                            <DeleteIcon sx={{ color: canEditPage('invitations') && canInviteUsers ? 'error.main' : 'grey.500', fontSize: '1rem' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ));
            })()}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ 
        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
        borderRadius: '0 0 12px 12px'
      }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={invitations.filter(invitation => invitation.status === 'pending').length}
          rowsPerPage={invitationsRowsPerPage}
          page={invitationsPage}
          onPageChange={handleInvitationsChangePage}
          onRowsPerPageChange={handleInvitationsChangeRowsPerPage}
          sx={{ 
            px: 3,
            py: 2,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.9rem',
              fontWeight: 500,
              color: theme.palette.text.primary
            },
            '& .MuiTablePagination-select': {
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              borderRadius: 2,
              px: 2,
              py: 1,
              fontWeight: 500,
              fontSize: '0.9rem',
              background: 'white',
              '&:hover': {
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
              }
            },
            '& .MuiTablePagination-actions': {
              '& .MuiIconButton-root': {
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                borderRadius: 2,
                margin: '0 2px',
                background: 'white',
                color: theme.palette.primary.main,
                fontWeight: 500,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: theme.palette.primary.main,
                  color: 'white',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                },
                '&.Mui-disabled': {
                  background: alpha(theme.palette.grey[300], 0.5),
                  color: theme.palette.grey[500],
                  border: `1px solid ${theme.palette.grey[300]}`,
                  transform: 'none',
                  boxShadow: 'none'
                }
              }
            }
          }}
        />
      </Box>
    </Paper>

    {/* Invite User Dialog */}
    <Dialog 
      open={inviteDialogOpen} 
      onClose={() => setInviteDialogOpen(false)} 
      maxWidth="sm" 
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 500,
        fontSize: '1.5rem',
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        pb: 3,
        mb: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        color: 'primary.main'
      }}>
        Invite New User
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 4 }}>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={inviteForm.email}
            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            sx={{ 
              '& .MuiInputLabel-root': { fontWeight: 500 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                background: alpha(theme.palette.grey[50], 0.8),
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5)
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: '2px'
                }
              }
            }}
          />
          
          <Box>
            <Typography variant="body2" sx={{ 
              mb: 1, 
              fontWeight: 500, 
              color: 'text.secondary',
              fontSize: '0.9rem'
            }}>
              Role
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={openRoleSelectionDialog}
              sx={{
                height: 56,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: alpha(theme.palette.grey[50], 0.8),
                color: 'text.primary',
                fontWeight: 500,
                textTransform: 'none',
                justifyContent: 'flex-start',
                px: 2,
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                }
              }}
            >
              <Box sx={{ textAlign: 'left', flex: 1 }}>
                {inviteForm.role_id ? (
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {roles.find(r => r.id === inviteForm.role_id)?.display_name || 'Unknown Role'} (Level {roles.find(r => r.id === inviteForm.role_id)?.hierarchy_level || 'N/A'})
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a role...
                  </Typography>
                )}
              </Box>
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: alpha(theme.palette.grey[50], 0.5),
        gap: 2
      }}>
        <Button 
          onClick={() => setInviteDialogOpen(false)} 
          sx={{ 
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            py: 1.2,
            color: 'text.secondary',
            '&:hover': {
              background: alpha(theme.palette.grey[400], 0.1)
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleInviteUser}
          disabled={!inviteForm.email || !inviteForm.role_id}
          sx={{
            borderRadius: 3,
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(10px)',
            color: 'text.secondary',
            fontWeight: 500,
            px: 4,
            py: 1.2,
            '&:hover': { 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              backgroundColor: 'grey.400',
              color: 'grey.600'
            }
          }}
        >
          Send Invitation
        </Button>
      </DialogActions>
    </Dialog>

    {/* Role Selection Dialog */}
    <Dialog 
      open={roleSelectionDialogOpen} 
      onClose={closeRoleSelectionDialog} 
      maxWidth="md" 
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)',
          minHeight: '70vh',
          maxHeight: '85vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 500,
        fontSize: '1.5rem',
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        pb: 3,
        mb: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        color: 'primary.main'
      }}>
        Select Role
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          
          {/* Search and Filter Section */}
          <Paper sx={{ 
            p: 3, 
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`
          }}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontSize: '0.9rem'
                  }}>
                    Search Roles
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Search by name, display name, or description..."
                    value={roleSelectionSearchTerm}
                    onChange={(e) => setRoleSelectionSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        height: 48,
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        py: 1.5,
                        px: 1.5
                      },
                      '& .MuiOutlinedInput-root': {
                        height: 48,
                        '& fieldset': { border: 'none' },
                        '&:hover fieldset': { border: 'none' },
                        '&.Mui-focused fieldset': { border: 'none' }
                      }
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontSize: '0.9rem'
                  }}>
                    Hierarchy Level
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={roleSelectionHierarchyFilter}
                      onChange={(e) => setRoleSelectionHierarchyFilter(e.target.value)}
                      displayEmpty
                      sx={{
                        height: 48,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' }
                      }}
                    >
                      <MenuItem value="">
                        <Typography sx={{ color: 'text.secondary' }}>All Levels</Typography>
                      </MenuItem>
                      {[...new Set(roles.map(role => role.hierarchy_level))]
                        .sort((a, b) => a - b)
                        .filter(level => level > 1)
                        .map(level => (
                          <MenuItem key={level} value={level.toString()}>Level {level}</MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Roles List */}
          <Box sx={{ pr: 1 }}>
            {Object.entries(getFilteredRolesForSelection())
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([level, levelRoles]) => (
              <Box key={level} sx={{ mb: 3 }}>
                {/* Hierarchy Level Header */}
                <Paper sx={{ 
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'default',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.2)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={`Level ${level}`}
                      color={getRoleStatusColor(parseInt(level))}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                      {getHierarchyLevelName(parseInt(level))}
                    </Typography>
                    <Chip
                      label={`${levelRoles.length} ${levelRoles.length === 1 ? 'Role' : 'Roles'}`}
                      size="small"
                      sx={{ 
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: 'info.main',
                        fontWeight: 500
                      }}
                    />
                  </Box>
                </Paper>

                {/* Roles in this level */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {levelRoles.map((role) => (
                    <Paper
                      key={role.id}
                      onClick={() => handleRoleSelection(role)}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        cursor: 'pointer',
                        border: selectedRoleForDialog?.id === role.id 
                          ? `2px solid ${theme.palette.primary.main}` 
                          : `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: selectedRoleForDialog?.id === role.id 
                          ? alpha(theme.palette.primary.main, 0.08) 
                          : 'white',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                          border: `1px solid ${theme.palette.primary.main}`
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                              {role.display_name}
                            </Typography>
                            {role.is_system_role && (
                              <Chip
                                label="System"
                                size="small"
                                sx={{ 
                                  bgcolor: alpha(theme.palette.grey[600], 0.1),
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            )}
                          </Box>
                          {role.description && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                              {role.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: alpha(theme.palette.grey[50], 0.5),
        gap: 2
      }}>
        <Button 
          onClick={closeRoleSelectionDialog} 
          sx={{ 
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            py: 1.2,
            color: 'text.secondary',
            '&:hover': {
              background: alpha(theme.palette.grey[400], 0.1)
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={confirmRoleSelection}
          disabled={!selectedRoleForDialog}
          sx={{
            borderRadius: 3,
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(10px)',
            color: 'text.secondary',
            fontWeight: 500,
            px: 4,
            py: 1.2,
            '&:hover': { 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              backgroundColor: 'grey.400',
              color: 'grey.600'
            }
          }}
        >
          Select Role
        </Button>
      </DialogActions>
    </Dialog>
    </Box>
  );
}